import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link, useParams } from "react-router-dom";
import SeoHead from "@/seo/SeoHead";
import { buildBreadcrumbJsonLd, buildServiceJsonLd } from "@/seo/jsonLd";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";

export default function HizmetDetayPage() {
  const { slug } = useParams();
  const { content } = useSiteContent();
  const services = content.services.items;
  const detail = content.services.detail;
  const service = services.find((item) => item.slug === slug);

  if (!service) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "#09090b" }}>
        <SeoHead
          title="Hizmet Bulunamadı"
          description="Aradığınız hizmet detay sayfası bulunamadı."
          path="/hizmetler"
          noindex
        />
        <Navbar />
        <section className="pt-40 pb-24 bg-zinc-950">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h1 className="text-4xl font-bold text-white" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              Hizmet Bulunamadı
            </h1>
            <p className="text-zinc-400 mt-4">İstediğiniz hizmet sayfası bulunamadı.</p>
            <Link to="/hizmetler" className="inline-flex mt-8 items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-bold text-sm tracking-wider" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              Hizmetler Sayfasına Dön
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const related = services.filter((item) => item.slug !== service.slug).slice(0, 3);
  const pagePath = `/hizmetler/${service.slug}`;
  const jsonLd = [
    buildBreadcrumbJsonLd([
      { name: "Ana Sayfa", path: "/" },
      { name: "Hizmetler", path: "/hizmetler" },
      { name: service.title, path: pagePath },
    ]),
    buildServiceJsonLd({
      title: service.title,
      description: service.desc,
      path: pagePath,
      image: service.img,
    }),
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#09090b" }}>
      <SeoHead title={service.title} description={service.desc} path={pagePath} image={service.img.split("?")[0]} jsonLd={jsonLd} />
      <Navbar />

      <section className="relative pt-36 pb-16 overflow-hidden border-b border-zinc-800/60" {...editableAttrs(`services.items.${services.findIndex((s) => s.slug === slug)}`, "object")}>
        <div className="absolute inset-0 z-0">
          <img
            src={service.img}
            alt={service.title}
            className="w-full h-full object-cover"
            {...editableAttrs(`services.items.${services.findIndex((s) => s.slug === slug)}.img`, "image")}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/95 via-zinc-950/85 to-zinc-950/60"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/95 via-transparent to-zinc-950/40"></div>
        </div>
        <div className="absolute left-0 top-0 h-full w-1 bg-red-600 z-10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-4 text-sm">
            <Link to="/" className="text-zinc-500 hover:text-red-500 transition-colors">Ana Sayfa</Link>
            <span className="text-zinc-700">/</span>
            <Link to="/hizmetler" className="text-zinc-500 hover:text-red-500 transition-colors">Hizmetler</Link>
            <span className="text-zinc-700">/</span>
            <span className="text-red-500">{service.title}</span>
          </div>
          <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            Hizmet Detayı
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mt-3 leading-tight" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {service.title}
          </h1>
          <p className="text-zinc-300 text-base md:text-lg mt-5 max-w-3xl leading-relaxed">{service.desc}</p>
        </div>
      </section>

      <section className="py-16 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 p-7 md:p-8">
            <h2 className="text-white text-2xl font-bold mb-5" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              Bu Hizmette Neler Yapıyoruz?
            </h2>
            <ul className="space-y-3" {...editableAttrs(`services.items.${services.findIndex((s) => s.slug === slug)}.detail`, "array")}>
              {service.detail.map((item) => (
                <li key={item} className="flex items-start gap-3 text-zinc-300">
                  <i className="ri-check-double-line text-red-500 text-lg mt-0.5"></i>
                  <span className="text-sm md:text-base leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 space-y-6 border-t border-zinc-800 pt-6">
              <div>
                <h3 className="text-white text-lg font-bold mb-2" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                  Neden Önemli?
                </h3>
                <p className="text-zinc-300 text-sm md:text-base leading-relaxed" {...editableAttrs(`services.items.${services.findIndex((s) => s.slug === slug)}.why`, "textarea")}>
                  {service.why}
                </p>
              </div>

              <div>
                <h3 className="text-white text-lg font-bold mb-2" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                  İşlem Süreci
                </h3>
                <p className="text-zinc-300 text-sm md:text-base leading-relaxed" {...editableAttrs(`services.items.${services.findIndex((s) => s.slug === slug)}.process`, "textarea")}>
                  {service.process}
                </p>
              </div>

              <div>
                <h3 className="text-white text-lg font-bold mb-2" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                  Kimler İçin Uygun?
                </h3>
                <ul className="space-y-2" {...editableAttrs(`services.items.${services.findIndex((s) => s.slug === slug)}.suitableFor`, "array")}>
                  {service.suitableFor.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-zinc-300 text-sm md:text-base">
                      <i className="ri-checkbox-circle-line text-red-500 mt-0.5"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-zinc-800/40 border border-zinc-700/50 p-4" {...editableAttrs(`services.items.${services.findIndex((s) => s.slug === slug)}.note`, "textarea")}>
                <p className="text-zinc-200 text-sm md:text-base leading-relaxed">
                  <span className="text-red-500 font-semibold">Not:</span> {service.note}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 p-7" {...editableAttrs("services.detail.quickContactTitle", "text")}>
            <h3 className="text-white text-xl font-bold mb-4" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              {detail.quickContactTitle}
            </h3>
            <div className="flex flex-col gap-3">
              <Link to="/iletisim" className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3.5 font-bold tracking-wider" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                <i className="ri-calendar-check-line"></i>
                {detail.quickContactButtonLabel}
              </Link>
              <a href={content.navigation.whatsappUrl} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 border border-zinc-600 hover:border-red-600 text-zinc-200 hover:text-white py-3.5 font-bold tracking-wider" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                <i className="ri-whatsapp-line"></i>
                {detail.quickContactWhatsappLabel}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-zinc-900 border-t border-zinc-800/60" {...editableAttrs("services.detail.relatedTitle", "text")}>
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-white text-2xl font-bold mb-6" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {detail.relatedTitle}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {related.map((item) => (
              <Link key={item.slug} to={`/hizmetler/${item.slug}`} className="bg-zinc-800/50 border border-zinc-700 hover:border-red-600/50 transition-colors p-5">
                <div className="text-red-500 text-xs tracking-widest mb-2" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                  {item.id}
                </div>
                <h4 className="text-white font-bold mb-2" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                  {item.title}
                </h4>
                <p className="text-zinc-400 text-sm line-clamp-2">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-16 md:py-24 border-t border-zinc-800/60 overflow-hidden" {...editableAttrs("services.detail.processSteps", "array")}>
        <div className="absolute inset-0 z-0">
          <img
            src={service.img}
            alt=""
            className="w-full h-full object-cover opacity-20"
            {...editableAttrs(`services.items.${services.findIndex((s) => s.slug === slug)}.img`, "image")}
          />
          <div className="absolute inset-0 bg-zinc-950/92"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/80 via-zinc-950/70 to-zinc-950/90"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-10 md:mb-12">
            <div className="inline-flex items-center gap-3 mb-3">
              <span className="w-10 h-0.5 bg-red-600 rounded-full" aria-hidden />
              <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                {detail.processSectionBadge}
              </span>
              <span className="w-10 h-0.5 bg-red-600 rounded-full" aria-hidden />
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-3" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              {detail.processSectionTitle}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6">
            {detail.processSteps.map((step, idx) => (
              <div key={step.title} className="relative bg-zinc-900/70 border border-zinc-800 hover:border-red-600/50 transition-colors p-5 md:p-6 backdrop-blur-[1px]">
                <div className="w-12 h-12 flex items-center justify-center bg-red-600 text-white mb-4 mx-auto">
                  <i className={`${step.icon} text-xl`}></i>
                </div>
                <h3 className="text-white font-bold text-lg leading-snug mb-3 text-center" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                  {step.title}
                </h3>
                <p className="text-zinc-300 text-sm leading-relaxed text-center">{step.text}</p>
                <div className="mt-4 text-red-500/80 text-xs font-bold tracking-widest text-center">ADIM {idx + 1}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
